<script>
  import Chart from "svelte-chartjs/src/Bar.svelte";
  import { createEventDispatcher, onMount } from "svelte";
  import { generateCanvasGradient, makeWebsiteRequest, parseNumber, parsePercent, months, monthsShort } from "../../Common/tools";
  import {External, M} from "../../external";

  export let filters = [],
    grouping = "daily",
    funnel = null,
    period = null;

  const revenueGradientColors = ["#FF55B8", "#FF8787"];
  const countsGradientColors = ["#3da6ff", "#257cc6"];
  const countBgGradientColors = ["#3da6ff", "#3da6ff"];

  let chartDataLabels = [];
  let monthLabels = [];
  let showMonthsBelow = false;
  let chartWrapper = null;

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: false,
      title: false
    },
    scales: {
      xAxes: {
        display: false
      },
      yAxisRevenue: {
        type: "linear",
        position: "left",
        display: false
      },
      yAxisSubscriptions: {
        type: "linear",
        position: "right",
        display: false
      }
    },
    layout: {
      padding: 3
    }
  };

  let chartData = {
    labels: [],
    datasets: []
  };

  let revenueChartValues = [];
  let subscriptionChartValues = [];
  let stats = {};
  let lifetimeChurnPercent = 0;
  let lifetimeChurnCount = 0;

  const dispatch = createEventDispatcher();

  export async function reload() {
    let from, to;
    if (period && period.from) {
      from = External.Tools.dateToISOStr(period.from);
    }
    if (period && period.to) {
      to = External.Tools.dateToISOStr(period.to);
    }
    if (!from) {
      let firstOrderTime = External.Website.firstOrderTime;
      from = External.Tools.dateToISOStr(new Date(firstOrderTime || External.createdTime * 1000));
    }
    if (!to) {
      to = External.Tools.dateToISOStr(new Date());
    }

    let response = await makeWebsiteRequest({
      action: "getOrderStats",
      method: "post",
      data: {
        type: "subscriptions",
        grouping: grouping,
        funnel: funnel,
        from: from,
        to: to,
        filters: JSON.stringify(filters)
      }
    });

    if (!response.data) {
      return;
    }
    stats = response.data || {};

    let subscriptionsDataset = stats.groupedSubscriptions || {};
    let revenueDataset = stats.groupedRevenue || {};

    let allSubs = stats.totalFinishedSubscriptions + stats.totalCanceledSubscriptions + stats.totalActiveSubscriptions;
    lifetimeChurnPercent = allSubs == 0 ? 0 : stats.totalCanceledSubscriptions / allSubs;
    lifetimeChurnCount = stats.totalCanceledSubscriptions;

    chartDataLabels = Object.keys(revenueDataset);
    monthLabels = [];
    showMonthsBelow = chartDataLabels.length > (chartWrapper.offsetWidth > 610 ? 14 : 7);
    chartDataLabels = chartDataLabels.map((label) => {
      let date;
      switch (grouping) {
        case "hourly":
          let hour = External.WebPlatform.parse(label);
          if (External.WebsiteSettings.getDateFormat() === "uk") {
            return hour;
          }
          if (hour === 0) {
            return "12";
          }
          if (hour > 12) {
            return hour - 12;
          }
          return hour;
        case "daily":
          date = new Date(label);
          if (showMonthsBelow) {
            let month = months[date.getMonth()];
            if (monthLabels.length === 0 || monthLabels[monthLabels.length - 1] !== month) {
              monthLabels.push(month);
            }
            return date.getDate();
          } else {
            return External.WebPlatform.dateToStrWithMonth(date, true, false, true, monthsShort);
          }
        case "monthly":
          date = new Date(label);
          return External.WebPlatform.dateToStrWithMonth(date, false, false, false, monthsShort);
        case "yearly":
          return label;
      }
      return label;
    });

    revenueChartValues = Object.values(revenueDataset);
    subscriptionChartValues = Object.values(subscriptionsDataset);
    let revenueDatapoints = revenueChartValues.map((el) => el.second);
    let subscriptionsDatapoints = subscriptionChartValues.map((el) => el.first);

    let canvas = document.querySelector(".analytics-box-big canvas");
    let revenueGradient = generateCanvasGradient(canvas, revenueGradientColors);
    let countGradient = generateCanvasGradient(canvas, countsGradientColors);
    let countBgGradient = generateCanvasGradient(canvas, countBgGradientColors);

    chartData = {
      labels: chartDataLabels,
      datasets: [
        {
          label: "Revenue",
          data: revenueDatapoints,
          backgroundColor: revenueGradient,
          pointRadius: 0,
          pointHoverRadius: 0,
          tension: 0.1,
          lineTension: 0.3,
          fill: true,
          borderRadius: 3,
          borderColor: "transparent",
          yAxisID: "yAxisRevenue",
          order: 1
        },
        {
          label: "Subscriptions",
          data: subscriptionsDatapoints,
          backgroundColor: countBgGradient,
          pointRadius: 0,
          pointHoverRadius: 0,
          tension: 0.1,
          lineTension: 0.3,
          fill: false,
          borderRadius: 1,
          borderColor: countGradient,
          yAxisID: "yAxisSubscriptions",
          type: "line",
          order: 0
        }
      ]
    };
  }

  let showReferers = () => {
    dispatch("showreferers");
  };

  onMount(() => {
    reload();
  });
</script>

<div class="flex col20 f-left store-section-analytics-warapper narrow-analytics-boxes flex-col-mobile" style="background: rgb(245, 246, 250); margin-top: 0px;">
  <div class="analytics-box analytics-box-small col5">
    <h3 class="flex align-center">{M.NEW_SUBSCRIPTIONS} <span class="chart-color ml-10" style="background: #3da6ff" /></h3>
    <h2 class="col20 f-left f-28 gray-text"><b>{stats.newActiveSubscriptions || 0}</b></h2>
  </div>
  <div class="analytics-box analytics-box-small col5">
    <h3 class="flex align-center">
      {M.CANCELATIONS}
      <span class="material-icons-outlined ml-5 stats-info" style="font-size: 20px;">
        info
        <span class="none-soft hidden-info">
          <h4>{M.CANCELATIONS}</h4>
          <span>{M.CANCELATIONS_DESK}</span>
        </span>
      </span>
    </h3>
    <h2 class="col20 f-left f-28 gray-text">
      <b>
        {stats.newCanceledSubscriptions || 0}
        <span class="decrease none">{lifetimeChurnCount || 0} ({parsePercent(lifetimeChurnPercent)}) lifetime</span>
      </b>
    </h2>
  </div>
  <div class="analytics-box analytics-box-small">
    <h3 class="flex align-center">
      {M.NEW_MRR}
      <span class="material-icons-outlined ml-5 stats-info" style="font-size: 20px;">
        info
        <span class="none-soft hidden-info">
          <h4>{M.MRR}</h4>
          <span>{M.MRR_DESK}</span>
        </span>
      </span>
    </h3>
    <h2 class="col20 f-left f-28 gray-text"><b>{External.Tools.currencyToStrWithSymbol(stats.newMrr)}</b></h2>
  </div>
  <div class="analytics-box analytics-box-small col5">
    <h3 class="flex align-center">
      {M.REVENUE}
      <span class="material-icons-outlined ml-5 stats-info" style="font-size: 20px;">
        info
        <span class="none-soft hidden-info">
          <h4>{M.REVENUE}</h4>
          <span>{M.REVENUE_DESC}</span>
        </span>
      </span>
      <span class="chart-color ml-10" style="background: #f97f7f" />
    </h3>
    <h2 class="col20 f-left f-28 gray-text"><b>{External.Tools.currencyToStrWithSymbol(stats.newRevenue)}</b></h2>
  </div>
</div>
<div class="flex f-left col20 mt-25 flex-col-mobile">
  <div class="analytics-box-big three-fourth no-bpadding-mobile">
    <h2 class="col20 f-left f-28 gray-text">{M.SUBSCRIPTIONS_REVENUE}</h2>
    <div class="col20 f-left relative mt-25 modern-chart-box" bind:this={chartWrapper}>
      <Chart data={chartData} options={chartOptions} />
      <div class="axis">
        {#each chartDataLabels as label, i}
          <div class="tick">
            <span class="tick-label">{label}</span>
            <span class="value value-first flex align-center with-color"> <span class="chart-color" style="background: #3da6ff" />{subscriptionChartValues[i].first}</span>
            <span class="value value-second flex align-center with-color">
              <span class="chart-color" style="background: #f97f7f" />{External.Tools.currencyToStrWithSymbol(revenueChartValues[i].second)}</span>
          </div>
        {/each}
      </div>
      {#if grouping === "hourly"}
        <div class="col20 f-left flex flex-row am-pm-row">
          <span class="flex-1 ta-center"><p>AM</p></span>
          <span class="flex-1 ta-center"><p>PM</p></span>
        </div>
      {:else if grouping === "daily" && showMonthsBelow}
        <div class="col20 f-left flex flex-row chart-label-row">
          {#each monthLabels as label, i}
            <span
              class="flex-1"
              class:ta-left={i === 0 && monthLabels.length > 1}
              class:ta-right={i === monthLabels.length - 1 && monthLabels.length > 1}
              class:ta-center={monthLabels.length === 1 || (i > 1 && i < monthLabels.length - 1)}>
              <p>{label}</p>
            </span>
          {/each}
        </div>
      {/if}
    </div>
  </div>
  <div class="one-fourth-analytics f-right">
    <div class="analytics-box">
      <div class="analytics-box analytics-box-small col20 analytics-box-small-clear">
        <h3>{M.TOTAL_MRR}</h3>
        <h2 class="col20 f-left f-28 gray-text">
          <b>{External.Tools.currencyToStrWithSymbol(stats.totalSubscriptionsMrr)}</b>
          <span class="col20 f-left mt-5" style="margin-left: 0; opacity: 0.7;">
            {stats.totalActiveSubscriptions || 0} {M.ACTIVE_SUBSCRIPTIONS}{#if stats.totalFinishedSubscriptions}<br /> {stats.totalFinishedSubscriptions} {M.COMPLETED_SUBSCRIPTIONS}{/if}
          </span>
        </h2>
      </div>
      <div class="analytics-box analytics-box-small col20 analytics-box-small-clear mt-35">
        <h3 class="flex align-center">
          {M.ARPU}
          <span class="material-icons-outlined ml-5 stats-info" style="font-size: 20px;">
            info
            <span class="none-soft hidden-info">
              <h4>{M.AVERAGE_REVENUE}</h4>
              <span>{M.AVERAGE_REVENUE_DESC}</span>
            </span>
          </span>
        </h3>
        <h2 class="col20 f-left f-28 gray-text">
          <b>{External.Tools.currencyToStrWithSymbol(stats.totalSubscriptionsMrr / stats.totalCustomers)}</b><span>/{M.MONTH}</span>
          <span class="col20 f-left mt-5" style="margin-left: 0; opacity: 0.7;">{stats.totalCustomers || 0} {M.SUBSCRIBERS}</span>
        </h2>
      </div>
      <div class="analytics-box analytics-box-small col20 analytics-box-small-clear mt-35">
        <h3>{M.TOTAL_CANCELATIONS}</h3>
        <h2 class="col20 f-left f-28 gray-text">
          <b>{lifetimeChurnCount || 0}</b><span>({parsePercent(lifetimeChurnPercent)})</span>
        </h2>
      </div>
    </div>
  </div>
</div>
